// src/components/modals/EditUserModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, User, Shield, Building, ChevronDown } from 'lucide-react'
import UserService from '../../services/userService'
import BranchService from '../../services/branchService'

const EditUserModal = ({ isOpen, onClose, user, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    username: '',
    role: '',
    branchId: '',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)

  // Get user roles from service
  const userRoles = UserService.getUserRoles()

  // Initialize form data when modal opens or user changes
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        username: user.username || '',
        role: user.role || '',
        branchId: user.branchId || '',
        isActive: user.isActive !== false // Default to true if undefined
      })
      setErrors({})
      fetchBranches()
    }
  }, [isOpen, user])

  // Fetch branches for dropdown
  const fetchBranches = async () => {
    try {
      setBranchesLoading(true)
      const response = await BranchService.getAllBranches()
      
      if (response && response.success) {
        const branchesData = response.data?.branches || []
        // Only show active branches
        const activeBranches = branchesData.filter(branch => branch.isActive)
        setBranches(activeBranches)
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      setBranches([])
    } finally {
      setBranchesLoading(false)
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores'
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required'
    }

    // Branch validation (optional for admin role)
    if (formData.role !== 'admin' && !formData.branchId) {
      newErrors.branchId = 'Branch is required for this role'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)
      
      // Prepare data for API - only send changed fields
      const updateData = {
        username: formData.username.trim(),
        role: formData.role,
        isActive: formData.isActive
      }
      
      // Only add branchId if it's not admin and a branch is selected
      if (formData.role !== 'admin' && formData.branchId) {
        updateData.branchId = formData.branchId
      }
      
      console.log('📤 Updating user:', {
        userId: user._id,
        updateData
      })
      
      const response = await UserService.updateUser(user._id, updateData)
      
      if (response.success) {
        console.log('✅ User updated successfully:', response)
        
        // Reset form
        setFormData({
          username: '',
          role: '',
          branchId: '',
          isActive: true
        })
        setErrors({})
        
        // Notify parent component with updated data
        const updatedUser = response.data?.user || { ...user, ...updateData }
        onUserUpdated(updatedUser)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Update failed with response:', response)
        // Handle API error
        if (response.errors) {
          setErrors(response.errors)
        } else {
          setErrors({ general: response.message || 'Failed to update user' })
        }
      }
    } catch (error) {
      console.error('❌ Error updating user:', error)
      
      // Enhanced error handling
      let errorMessage = 'Failed to update user. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'User not found. It may have been deleted by another user.'
      } else if (error.message.includes('duplicate') || error.message.includes('already exists')) {
        errorMessage = 'A user with this username already exists.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to update this user. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }
      
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit()
    }
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-3">
            {/* <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-orange-600" />
            </div> */}
            <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username *
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <User className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="akash@123"
                disabled={loading}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors.username ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
              />
            </div>
            {errors.username && (
              <p className="mt-1 text-sm text-red-600">{errors.username}</p>
            )}
          </div>

          {/* Role Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Type *
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Shield className="w-4 h-4 text-gray-400" />
              </div>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                disabled={loading}
                className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors.role ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
              >
                <option value="">Select role</option>
                {userRoles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role}</p>
            )}
          </div>

          {/* Branch */}
          {formData.role && formData.role !== 'admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch *
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <Building className="w-4 h-4 text-gray-400" />
                </div>
                <select
                  name="branchId"
                  value={formData.branchId}
                  onChange={handleInputChange}
                  disabled={loading || branchesLoading}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed ${
                    errors.branchId ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                  } ${branchesLoading ? 'bg-gray-50' : ''}`}
                >
                  <option value="">
                    {branchesLoading ? 'Loading branches...' : 'Select branch'}
                  </option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                {branchesLoading ? (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                ) : (
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                )}
              </div>
              {errors.branchId && (
                <p className="mt-1 text-sm text-red-600">{errors.branchId}</p>
              )}
            </div>
          )}

          {/* Status Toggle */}
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
              disabled={loading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500/20 ${
                formData.isActive ? 'bg-orange-600' : 'bg-gray-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                  formData.isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default EditUserModal